// src/services/matchingService.js

import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "../firebase";
import { evaluateLocationFit } from "../utils/locationUtils";
import { getLaneForCategory } from "../constants/lanes";
import { isSpecialist } from "../constants/roleLabels";
import { logger } from "../utils/logger";

/**
 * Expert/incident matching.
 *
 * The ranking answers one question: who should be on this incident in the next
 * few minutes. That makes it a function of expertise, track record, whether the
 * expert is awake, and how much they are already carrying — in that order.
 *
 * Three things the previous weighting got wrong:
 *
 *  - Geography carried 35% against expertise's 50%, on a product where the work
 *    happens over a shared screen. Physical distance is irrelevant; overlapping
 *    working hours are not. Timezone now carries 15% and continent nothing.
 *  - Availability leaned on a self-set `availability === "online"` profile flag,
 *    which is a claim rather than an observation. It is replaced by current
 *    load, which is derived from the expert's own open incidents.
 *  - Reputation was computed and displayed but never influenced the order, so a
 *    specialist who had abandoned half their incidents ranked identically to one
 *    who had closed all of them.
 *
 * Every score also carries `reasons`: short phrases explaining the placement.
 * A bare "87% match" is not evidence, and the customer is being asked to hand
 * over a production outage on the strength of it.
 */

const WEIGHTS = {
  expertise: 0.5,
  reliability: 0.2,
  workingHours: 0.15,
  capacity: 0.15,
};

/** Ranking threshold for flagging a match as strong. Never used to filter. */
export const STRONG_MATCH_SCORE = 0.7;

/** Firestore allows at most 10 values in an `in` filter. */
const IN_QUERY_LIMIT = 10;

const normalizeList = (values) =>
  (values || []).map((value) => String(value).toLowerCase().trim()).filter(Boolean);

function chunk(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * How much of this incident the expert actually covers.
 *
 * Scored as the share of the incident's tags the expert claims, because the
 * incident defines what is needed — an expert listing thirty skills is not a
 * better match than one listing the three that matter here.
 */
function scoreExpertise(incidentTags, expertSkills) {
  const tags = normalizeList(incidentTags);
  const skills = normalizeList(expertSkills);

  if (tags.length === 0) return { score: 0.5, matched: [], reason: null };
  if (skills.length === 0) return { score: 0, matched: [], reason: null };

  const matched = tags.filter((tag) => skills.includes(tag));
  const score = matched.length / tags.length;

  return {
    score,
    matched,
    reason:
      matched.length === tags.length
        ? `Covers all ${tags.length} incident tags`
        : `Covers ${matched.length} of ${tags.length} incident tags`,
  };
}

/**
 * Track record, from incidents the expert has already been given.
 *
 * Measured against decided incidents only. Work still in flight is not evidence
 * either way, and counting it as a failure would punish an expert for being busy
 * at the moment somebody looked.
 *
 * `stats` of null means "we were not allowed to look" rather than "nothing to
 * report", so the term goes neutral and states nothing. Claiming an established
 * expert is "new to the platform" because of a permissions boundary would be
 * worse than staying quiet.
 */
function scoreReliability(stats) {
  if (!stats) return { score: 0.5, reason: null };

  const decided = (stats.completedCount || 0) + (stats.unresolvedCount || 0);

  // Below a handful of outcomes the rate is noise, so it neither helps nor hurts.
  if (decided < 3) {
    return {
      score: 0.5,
      reason: stats.totalCount ? "Building a track record" : "New to the platform",
    };
  }

  const rate = stats.completedCount / decided;
  return {
    score: rate,
    reason: `${Math.round(rate * 100)}% resolved across ${decided} incidents`,
  };
}

/**
 * Spare attention. An expert already running three incidents is a worse choice
 * for a fourth than an idle one of equal skill, however good they are.
 */
function scoreCapacity(stats) {
  if (!stats) return { score: 0.5, reason: null };

  const active = stats.activeCount || 0;

  if (active === 0) return { score: 1, reason: "No incidents open right now" };
  if (active === 1) return { score: 0.85, reason: "Handling 1 other incident" };
  if (active === 2) return { score: 0.6, reason: "Handling 2 other incidents" };
  if (active === 3) return { score: 0.35, reason: "Handling 3 other incidents" };
  return { score: 0.15, reason: `Handling ${active} other incidents` };
}

/**
 * Workload counters denormalised onto the expert's user document.
 *
 * A customer's session cannot compute these: firestore.rules deliberately stops
 * one customer from enumerating every incident an expert has ever handled, and
 * a single unreadable document fails the whole query — so the honest options are
 * a maintained counter or nothing. See the "Expert workload" cases in
 * scripts/rules-harness.js.
 *
 * Returns null when the counters are absent, which keeps the reliability and
 * capacity terms neutral rather than inventing a track record.
 */
function readDenormalisedStats(expert) {
  const stats = expert?.stats;
  if (!stats || typeof stats !== "object") return null;

  const hasCounters = ["activeCount", "completedCount", "unresolvedCount"].some(
    (key) => typeof stats[key] === "number"
  );
  if (!hasCounters) return null;

  return {
    activeCount: stats.activeCount || 0,
    completedCount: stats.completedCount || 0,
    unresolvedCount: stats.unresolvedCount || 0,
    totalCount:
      stats.totalCount ||
      (stats.activeCount || 0) +
        (stats.completedCount || 0) +
        (stats.unresolvedCount || 0),
  };
}

/**
 * Combines the four terms for one expert/incident pair.
 *
 * @param {object} incident       Problem document.
 * @param {object} expert         User document for the expert.
 * @param {object} [context]
 * @param {object} [context.client] Client document, for working-hours overlap.
 * @param {object} [context.stats]  Workload stats from fetchExpertWorkloadStats.
 */
export const computeMatchScore = (incident, expert, context = {}) => {
  const { client, stats } = context;

  const expertise = scoreExpertise(incident?.tags, expert?.skills);
  const reliability = scoreReliability(stats);
  const capacity = scoreCapacity(stats);
  const workingHours = evaluateLocationFit(
    client?.location || incident?.clientLocation,
    expert?.location
  );

  const score =
    expertise.score * WEIGHTS.expertise +
    reliability.score * WEIGHTS.reliability +
    workingHours.score * WEIGHTS.workingHours +
    capacity.score * WEIGHTS.capacity;

  // Ordered by how much they should influence the reader's decision.
  const reasons = [
    expertise.reason,
    workingHours.known ? workingHours.label : null,
    reliability.reason,
    capacity.reason,
  ].filter(Boolean);

  return {
    score: Math.min(score, 1),
    isStrongMatch: score >= STRONG_MATCH_SCORE,
    reasons,
    breakdown: {
      expertise: expertise.score,
      matchedTags: expertise.matched,
      reliability: reliability.score,
      workingHours: workingHours.score,
      hoursApart: workingHours.hoursApart,
      capacity: capacity.score,
      activeCount: stats?.activeCount || 0,
    },
  };
};

/**
 * Active and decided incident counts, in one round trip per ten experts rather
 * than one per expert.
 *
 * Only usable for an expert reading their *own* workload. A customer's session
 * is denied this query by design, so the customer-facing ranking reads the
 * denormalised counters on the user document instead.
 *
 * @param {string[]} expertIds
 * @returns {Promise<Record<string, object>>} Keyed by expert id.
 */
export async function fetchExpertWorkloadStats(expertIds = []) {
  const ids = [...new Set(expertIds.filter(Boolean))];
  const stats = {};
  ids.forEach((id) => {
    stats[id] = {
      activeCount: 0,
      completedCount: 0,
      unresolvedCount: 0,
      totalCount: 0,
    };
  });

  if (ids.length === 0) return stats;

  const batches = await Promise.all(
    chunk(ids, IN_QUERY_LIMIT).map(async (group) => {
      try {
        const snap = await getDocs(
          query(collection(db, "problems"), where("helperId", "in", group))
        );
        return snap.docs.map((d) => d.data());
      } catch (error) {
        // Reputation is decoration on a queue, not a precondition for it.
        logger.log("Could not load expert workload:", error.message);
        return [];
      }
    })
  );

  batches.flat().forEach((incident) => {
    const entry = stats[incident.helperId];
    if (!entry) return;

    entry.totalCount += 1;
    if (incident.status === "completed") entry.completedCount += 1;
    else if (incident.status === "unresolved") entry.unresolvedCount += 1;
    else entry.activeCount += 1;
  });

  return stats;
}

/** Fetches user documents once each, however many times they are asked for. */
function createUserCache() {
  const cache = new Map();

  return async function loadUser(userId) {
    if (!userId) return null;
    if (cache.has(userId)) return cache.get(userId);

    const promise = getDoc(doc(db, "users", userId))
      .then((snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null))
      .catch((error) => {
        logger.log(`Could not load user ${userId}:`, error.message);
        return null;
      });

    cache.set(userId, promise);
    return promise;
  };
}

/**
 * Ranks experts for one incident.
 *
 * @returns {Promise<{problem: object, matches: Array}>} `matches` carry
 *   `{ helper, score, isStrongMatch, reasons, breakdown, stats }`.
 */
export const getTopHelpersForProblem = async (problemId, limit = 3) => {
  const problemSnap = await getDoc(doc(db, "problems", problemId));
  if (!problemSnap.exists()) throw new Error("Problem not found");
  const problem = { id: problemSnap.id, ...problemSnap.data() };

  const tags = normalizeList(problem.tags).slice(0, IN_QUERY_LIMIT);
  if (tags.length === 0) {
    logger.log("Incident has no tags; cannot rank experts.");
    return { problem, matches: [] };
  }

  // Filtered on skills only, with the role checked in memory afterwards. The
  // query used to carry `where("role", "==", "helper")`, but experts are stored
  // under the canonical role "specialist" (see constants/roleLabels.js), so
  // every expert who signed up after that rename was silently invisible to
  // customers. Firestore will not accept `in` alongside `array-contains-any`,
  // so the role cannot be a second clause here even if we listed both values.
  const candidateSnap = await getDocs(
    query(collection(db, "users"), where("skills", "array-contains-any", tags))
  );

  // One entry per expert. The users collection has historically carried both
  // `uid` and doc-id identifiers for the same person, so collapse on identity
  // before scoring rather than de-duplicating the ranked output afterwards.
  const byIdentity = new Map();
  candidateSnap.docs.forEach((snap) => {
    const data = { id: snap.id, ...snap.data() };
    if (!isSpecialist(data.role)) return;

    const key = data.uid || data.id || data.email;
    if (key && !byIdentity.has(key)) byIdentity.set(key, data);
  });
  const candidates = [...byIdentity.values()];

  if (candidates.length === 0) return { problem, matches: [] };

  const loadUser = createUserCache();
  const client = await loadUser(problem.clientId);

  const matches = candidates
    .map((helper) => {
      const stats = readDenormalisedStats(helper);
      const result = computeMatchScore(problem, helper, {
        client: client || { location: problem.clientLocation },
        stats,
      });
      return { helper, stats, ...result };
    })
    // An expert with no overlapping tag is not a weak match, they are the wrong
    // person. Everything else stays and is ranked.
    .filter((match) => match.breakdown.matchedTags.length > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return { problem, matches };
};

/**
 * Ranks open incidents for one expert.
 *
 * Every incident sharing a tag with the expert is returned, ordered by score.
 * A weak location or capacity term pushes an incident down the queue but never
 * removes it: silently hiding a genuine tag match is how an incident sits
 * unclaimed while a qualified expert stares at an empty queue.
 *
 * @returns {Promise<{problems: Array, total: number}>}
 */
export const getSuggestedOpenProblemsForHelper = async (
  helperOrId,
  limit = 10
) => {
  const loadUser = createUserCache();

  let helper = helperOrId;
  if (typeof helperOrId === "string") {
    helper = await loadUser(helperOrId);
    if (!helper) throw new Error("Helper not found");
  }

  const helperId = helper.uid || helper.id;
  const helperSkills = normalizeList(helper?.skills);

  if (helperSkills.length === 0) {
    logger.log("Expert has no skills recorded; no incidents can be suggested.");
    return { problems: [], total: 0 };
  }

  const openSnap = await getDocs(
    query(collection(db, "problems"), where("status", "==", "open"))
  );

  // Narrow on tags before doing any I/O per incident.
  const tagMatched = openSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((incident) =>
      normalizeList(incident.tags).some((tag) => helperSkills.includes(tag))
    );

  if (tagMatched.length === 0) {
    logger.log(
      `No open incident shares a tag with this expert (${openSnap.size} open).`
    );
    return { problems: [], total: 0 };
  }

  // The client profile only affects working-hours overlap, and several incidents
  // often share a client. Previously this issued one read per incident inside a
  // map, so a busy queue meant dozens of duplicate reads of the same document.
  const [clients, stats] = await Promise.all([
    Promise.all(
      [...new Set(tagMatched.map((i) => i.clientId).filter(Boolean))].map(
        async (clientId) => [clientId, await loadUser(clientId)]
      )
    ),
    fetchExpertWorkloadStats([helperId]),
  ]);
  const clientById = new Map(clients);

  const ranked = tagMatched
    .map((incident) => {
      const result = computeMatchScore(incident, helper, {
        client:
          clientById.get(incident.clientId) || {
            location: incident.clientLocation,
          },
        stats: stats[helperId],
      });

      return {
        problem: incident,
        matchScore: result.score,
        isStrongMatch: result.isStrongMatch,
        matchReasons: result.reasons,
        breakdown: result.breakdown,
        lane: getLaneForCategory(incident.categoryId || incident.category),
      };
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);

  logger.log(
    `Suggested ${ranked.length} of ${tagMatched.length} tag-matched incidents ` +
      `(${ranked.filter((r) => r.isStrongMatch).length} strong).`
  );

  return { problems: ranked, total: ranked.length };
};
